import { ApplicationFeatures, ApplicationSettings, CompanyInfo, EmployeePortalSettings, partners, SiteSettings } from "../../../DTOs";
import { basicValidation } from '../basicValidation';

export const miscFieldValidation = {
    site: {
        // 32 max characters, no hyphens (pg)
        // 3 characters minimum, and no underscores (azure blob containers)
        id: (value: string) => validateSiteId(value),
        loginName: (value: string) => validateSiteId(value),
        partner: (value: string | null) =>
        {
            if (value === null || value === '') return true;

            if (!basicValidation.validateString(value, false, false, 1, 100)) return false;
            if (!partners.some(p => p === value)) return false;

            return true;
        },
        settings: (value: SiteSettings) =>
        {
            if (!value) return false;
            if (!basicValidation.validateBoolean(value.enabled)) return false;
            if (!basicValidation.validateBoolean(value.hideSurveyEmailAddresses)) return false;
            if (!basicValidation.validateTextString(value.apiVideoKey, 0, 100)) return false;

            if (!Array.isArray(value.features)) return false;
            if (value.features.some(f => !basicValidation.validateTextString(f, 0, 100))) return false;
            if (value.features.some(f => !ApplicationFeatures.includes(f))) return false;
            if (!basicValidation.arrayStringsAreUnique(value.features)) return false;

            return true;
        },
        companyInfo: (value: CompanyInfo) =>
        {
            if (!value) return false;
            if (!basicValidation.validateTextString(value.name, 0, 100)) return false;
            if (!basicValidation.validateTextString(value.address, 0, 100)) return false;
            if (!basicValidation.validateTextString(value.altAddress, 0, 100)) return false;
            if (!basicValidation.validateTextString(value.city, 0, 100)) return false;
            if (!basicValidation.validateTextString(value.zip, 0, 100)) return false;
            if (!basicValidation.validateTextString(value.phone, 0, 100)) return false;
            if (!basicValidation.validateTextString(value.website, 0, 100)) return false;
            if (!basicValidation.validateTextString(value.note, 0, 5000)) return false;
            return true;
        },
        applicationSettings: (value: ApplicationSettings) =>
        {
            return miscFieldValidation.site.applicationSettingsObject(value)
                && miscFieldValidation.site.applicationSettingsEmployeePortalSettings(value)
                && miscFieldValidation.site.applicationSettingsEmployeePortalSettingsEnabled(value)
                && miscFieldValidation.site.applicationSettingsEmployeePortalSettingsResetTimeStamp(value)
                && miscFieldValidation.site.applicationSettingsEmployeePortalSettingsAuthentication(value)
                && miscFieldValidation.site.applicationSettingsEmployeePortalSettingsMagicLink(value)
                && miscFieldValidation.site.applicationSettingsEmployeePortalSettingsMagicLinkEnabled(value)
                && miscFieldValidation.site.applicationSettingsEmployeePortalSettingsMagicLinkValidDomains(value)
                && miscFieldValidation.site.applicationSettingsEmployeePortalSettingsPublicKey(value)
                && miscFieldValidation.site.applicationSettingsEmployeePortalSettingsPublicKeyEnabled(value)
                && miscFieldValidation.site.applicationSettingsEmployeePortalSettingsPublicKeyAccessKey(value);
        },
        applicationSettingsObject: (value: ApplicationSettings) =>
        {
            return !!value;
        },
        applicationSettingsEmployeePortalSettings: (value: ApplicationSettings) =>
        {
            return !!value?.employeePortalSettings;
        },
        applicationSettingsEmployeePortalSettingsEnabled: (value: ApplicationSettings) =>
        {
            return basicValidation.validateBoolean(value?.employeePortalSettings?.enabled as any);
        },
        applicationSettingsEmployeePortalSettingsResetTimeStamp: (value: ApplicationSettings) =>
        {
            const resetTimeStamp = value?.employeePortalSettings?.resetTimeStamp;
            return resetTimeStamp instanceof Date && basicValidation.date(resetTimeStamp);
        },
        applicationSettingsEmployeePortalSettingsAuthentication: (value: ApplicationSettings) =>
        {
            return !!value?.employeePortalSettings?.authentication;
        },
        applicationSettingsEmployeePortalSettingsMagicLink: (value: ApplicationSettings) =>
        {
            return !!value?.employeePortalSettings?.authentication?.magicLink;
        },
        applicationSettingsEmployeePortalSettingsMagicLinkEnabled: (value: ApplicationSettings) =>
        {
            return basicValidation.validateBoolean(value?.employeePortalSettings?.authentication?.magicLink?.enabled as any);
        },
        applicationSettingsEmployeePortalSettingsMagicLinkValidDomains: (value: ApplicationSettings) =>
        {
            const validDomains = value?.employeePortalSettings?.authentication?.magicLink?.validDomains;
            return Array.isArray(validDomains)
                && validDomains.every((d: any) => typeof d === 'string')
                && validDomains.every((d: string) => basicValidation.isValidEmailDomain(d))
                && basicValidation.arrayStringsAreUnique(validDomains);
        },
        applicationSettingsEmployeePortalSettingsPublicKey: (value: ApplicationSettings) =>
        {
            return !!value?.employeePortalSettings?.authentication?.publicKey;
        },
        applicationSettingsEmployeePortalSettingsPublicKeyEnabled: (value: ApplicationSettings) =>
        {
            return basicValidation.validateBoolean(value?.employeePortalSettings?.authentication?.publicKey?.enabled as any);
        },
        applicationSettingsEmployeePortalSettingsPublicKeyAccessKey: (value: ApplicationSettings) =>
        {
            const accessKey = value?.employeePortalSettings?.authentication?.publicKey?.accessKey;
            return typeof accessKey === 'string'
                && (accessKey === '' || new RegExp('^[a-z0-9]{32}$').test(accessKey));
        },
        employeePortalSettings: (value: EmployeePortalSettings) =>
        {
            return miscFieldValidation.site.applicationSettings({ employeePortalSettings: value });
        }
    },

    userNotification: {
        userId: (value: string) => basicValidation.resourceId.isValid(value),
        notificationId: (value: string) => basicValidation.resourceId.isValid(value)
    },

    confirmationRequest: {
        id: (value: string) => basicValidation.resourceId.isValid(value)
    },

    organizationOwner: {
        firstname: (value: string) => basicValidation.resourceText.isValid(value, true, 100),
        middlename: (value: string) => basicValidation.resourceText.isValid(value, true, 100),
        lastname: (value: string) => basicValidation.resourceText.isValid(value, true, 100)
    },

    workgroup: {
        id: (value: string) => basicValidation.resourceId.isValid(value),
        parentIdOrNone: (value: string) => basicValidation.resourceId.isValidOrEmpty(value),
        type: (value: 'committee' | 'workgroup') =>
        {
            if (!value)
                return false;
            return ['committee', 'workgroup'].find(v => v === value) !== undefined;
        },
        name: (value: string) => basicValidation.resourceText.isValid(value, false, 100)
    },
};

function validateSiteId (value: string): boolean
{
    // Although postgres allows _ in identifiers, Azure Blob Storage does not
    return typeof value === 'string'
        && value !== null
        && value !== undefined
        && new RegExp("^[a-z]{1}[a-z0-9]{2,31}$").test(value);
}